import type {
  DatesSetArg,
  EventClickArg,
  EventDropArg,
  MoreLinkArg
} from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin, { type DateClickArg } from "@fullcalendar/interaction"
import listPlugin from "@fullcalendar/list"
import FullCalendar from "@fullcalendar/react"
import timeGridPlugin from "@fullcalendar/timegrid"
import { Divider, Flex, Typography } from "antd"
import dayjs from "dayjs"
import React, { memo, useEffect, useRef, useState } from "react"

import type {
  CalendarFilters as CalendarFiltersType,
  CalendarModalData
} from "../../redux/calendar.types.ts"
import { type Event } from "../../types/types.ts"
import { parseCalendarFocusDate } from "../../helpers/calendar-deep-link.ts"
import { resolveNewEventSlot } from "../helpers/new-event-slot.ts"
import { transformEventsForCalendar } from "../helpers/transform-events-for-calendar"
import { CalendarEventCell } from "./CalendarEventCell/CalendarEventCell"
import { CalendarFilters } from "./CalendarFilters/CalendarFilters"
import { CalendarHeader } from "./CalendarHeader"
import { CalendarNavigation } from "./CalendarNavigation"
import "./calendar.less"

const { Text } = Typography

interface CalendarProps {
  timezone: string
  focusDate?: string | null
  filters: CalendarFiltersType
  isMobile: boolean
  events: Event[]
  //
  createAllowed?: boolean
  editAllowed?: boolean
  //
  onSelectEvent: (id: string) => void
  onSetModalData: (data: CalendarModalData | null) => void
  onFetchEvents: ({ start, end }: Partial<DatesSetArg>) => void
  onFilterChange: (filter: Record<string, unknown>) => void
  onSetMoreInfo: (args: MoreLinkArg) => void
  onDropEvent: (arg: EventDropArg) => void
}

export const Calendar = memo(
  ({
    timezone,
    focusDate,
    filters,
    isMobile,
    createAllowed,
    editAllowed,
    events: apiEvents = [],
    onSelectEvent,
    onSetModalData,
    onFetchEvents,
    onFilterChange,
    onSetMoreInfo,
    onDropEvent
  }: CalendarProps) => {
    const isFirstRender = useRef(true)
    const calendarRef = useRef<FullCalendar | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const getAPI = () => calendarRef?.current?.getApi()

    const events = transformEventsForCalendar(apiEvents)
    const [currentView, setCurrentView] = useState(
      isMobile ? "listWeek" : "timeGridWeek"
    )

    const now = dayjs().tz(timezone).format()

    const handleDateClick = (arg: DateClickArg) => {
      onSetModalData(resolveNewEventSlot(arg.dateStr, timezone))
    }

    const handleEventClick = (clickInfo: EventClickArg) => {
      const event = clickInfo?.event
      const id = event?.id || event?.extendedProps?.id
      onSelectEvent(id)
    }

    const onPrevClick = () => {
      const calendarAPI = getAPI()
      if (calendarAPI) calendarAPI.prev()
    }

    const onNextClick = () => {
      const calendarAPI = getAPI()
      if (calendarAPI) calendarAPI.next()
    }

    const onTodayClick = () => {
      const calendarAPI = getAPI()

      if (calendarAPI) {
        calendarAPI.today()
        calendarAPI.gotoDate(calendarAPI.getDate())
      }
    }

    const onViewChange = (view: string) => {
      const calendarAPI = getAPI()
      setCurrentView(view)

      if (calendarAPI) calendarAPI.changeView(view)
    }

    const handleMoreLinkClick = (info: MoreLinkArg): any => {
      info.jsEvent.preventDefault()
      onSetMoreInfo(info)
      return () => null
    }

    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }

      if (timezone) {
        const calendarAPI = getAPI()
        const dateStr = dayjs().tz(timezone).format()

        if (calendarAPI) {
          calendarAPI.gotoDate(dateStr)
          calendarAPI.setOption("now", dateStr)
        }
      }
    }, [timezone])

    // A meeting opened from a Lead names its date in the URL, so the calendar
    // has to land on that page rather than on today.
    useEffect(() => {
      const target = parseCalendarFocusDate(focusDate)
      if (!target) return
      getAPI()?.gotoDate(target)
    }, [focusDate])

    useEffect(() => {
      const container = containerRef.current
      if (!container) return
      const resizeObserver = new ResizeObserver(() => getAPI()?.updateSize())
      resizeObserver.observe(container)
      return () => resizeObserver.disconnect()
    }, [])

    return (
      <Flex vertical className="size-full min-h-full" ref={containerRef}>
        <div>
          <CalendarHeader
            createAllowed={createAllowed}
            onSetModalData={onSetModalData}
          />
          <Divider size={"small"} className={"m-1.5"} />

          <CalendarFilters
            filters={filters}
            isMobile={isMobile}
            currentView={currentView}
            onViewChange={onViewChange}
            onFilterChange={onFilterChange}
            onSelectEvent={onSelectEvent}
          />
          <Divider size={"small"} className={"m-1.5"} />
        </div>
        <CalendarNavigation
          getAPI={getAPI}
          onTodayClick={onTodayClick}
          onPrevClick={onPrevClick}
          onNextClick={onNextClick}
        />

        <FullCalendar
          views={{
            timeGridDay: {
              titleFormat: { year: "numeric", month: "long", day: "numeric" } // "January 8, 2026"
            },
            listDay: {
              titleFormat: { year: "numeric", month: "long", day: "numeric" } // "January 8, 2026"
            },
            dayGridMonth: {
              titleFormat: { year: "numeric", month: "long" } // "January 2026"
            },
            timeGridWeek: {
              titleFormat: { year: "numeric", month: "short", day: "numeric" } // "Jan 6, 2026"
            },
            listWeek: {
              titleFormat: { year: "numeric", month: "short", day: "numeric" } // "Jan 6, 2026"
            },
            listMonth: {
              titleFormat: { year: "numeric", month: "short", day: "numeric" } // "Jan 6, 2026"
            }
          }}
          // handleWindowResize={false}
          datesSet={onFetchEvents}
          ref={calendarRef}
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            interactionPlugin,
            listPlugin
          ]}
          titleFormat={{
            weekday: "short",
            month: "short",
            day: "2-digit"
          }}
          dayHeaderContent={(args) => {
            // Use date directly without time zone conversion
            const date = dayjs.utc(args.date)
            // OR if you need a calendar timezone:
            // const date = dayjs(args.date).tz(args.view.dateEnv.timeZone, true)

            const type = args?.view?.type
            const isToday = args?.isToday

            if (["dayGridMonth"]?.includes(type)) {
              return <Text strong>{date.format("ddd")}</Text>
            }

            return (
              <Flex vertical className={"px-1"}>
                <Text
                  className={isToday ? "text-inherit font-bold" : ""}
                  strong
                >
                  {date.format("ddd")}
                </Text>
                <Text
                  type={"secondary"}
                  className={isToday ? "text-inherit font-bold" : ""}
                >
                  {date.format("MMM D")}
                </Text>
              </Flex>
            )
          }}
          initialView={currentView}
          now={now}
          initialDate={now}
          // locale={enGbLocale}
          // locales={[enGbLocale]}
          firstDay={0}
          droppable={false}
          editable={!!editAllowed}
          eventDurationEditable={false}
          //
          selectable={false}
          allDayText={""}
          eventOverlap
          slotEventOverlap
          selectMirror
          dayMaxEvents={2} // Для dayGridMonth
          dayMaxEventRows={3} // Альтернатива для dayGrid
          eventMaxStack={3} // Максимум стеків events в одному слоті
          eventMinHeight={20} // Мінімальна висота event
          weekends
          dragScroll={false}
          events={events}
          height="100%"
          timeZone={timezone}
          headerToolbar={false}
          eventMinWidth={300}
          slotMinWidth={300}
          slotMinTime="00:00:00"
          slotMaxTime="23:59:00"
          scrollTime={{ hours: 9 }}
          slotLabelFormat={{
            hour: "numeric",
            minute: "2-digit",
            hour12: true
          }}
          dayHeaderFormat={{
            weekday: "short",
            day: "numeric"
          }}
          eventTimeFormat={{
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            meridiem: "short"
          }}
          eventDidMount={(info) => {
            const event = info.event
            const el = info.el

            // Примусово застосувати кольори з події
            if (event.backgroundColor) {
              el.style.backgroundColor = event.backgroundColor
            }
            if (event.borderColor) {
              el.style.borderColor = event.borderColor
            }
            if (event.textColor) {
              el.style.color = event.textColor
            }

            // Для тексту всередині події
            const titleEl = el.querySelector(".fc-event-title") as HTMLElement

            if (titleEl && event.textColor) {
              titleEl.style.color = event.textColor
            }

            const timeEl = el.querySelector(".fc-event-time") as HTMLElement
            if (timeEl && event.textColor) {
              timeEl.style.color = event.textColor
            }
          }}
          dateClick={createAllowed ? handleDateClick : undefined}
          moreLinkClick={handleMoreLinkClick}
          eventClick={handleEventClick}
          eventContent={CalendarEventCell}
          eventDrop={onDropEvent}
          // eventResize={(info) => console.log("Event resized:", info)}
          // select={(props) => console.log("select:", props)}
        />
      </Flex>
    )
  }
)

Calendar.displayName = "Calendar"
